import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { ActiniumLoader } from "../components/ui/actinium-loader"

const PageLoadingContext = createContext(null)

/**
 * Fullscreen Actinium Book loader while any registered page fetch is in flight.
 * Uses a counter so overlapping loads stay visible until the last one finishes.
 */
export function PageLoadingProvider({ children }) {
  const [count, setCount] = useState(0)
  const [label, setLabel] = useState("Loading…")

  const begin = useCallback((message) => {
    if (message) setLabel(message)
    setCount((n) => n + 1)
  }, [])

  const end = useCallback(() => {
    setCount((n) => Math.max(0, n - 1))
  }, [])

  const value = useMemo(() => ({ begin, end }), [begin, end])

  return (
    <PageLoadingContext.Provider value={value}>
      {children}
      {count > 0 ? <ActiniumLoader fullscreen size="xl" label={label} /> : null}
    </PageLoadingContext.Provider>
  )
}

/**
 * Keep the global Actinium loader active while `active` is true.
 * Safe across remounts and overlapping callers via ref-counting in the provider.
 */
export function usePageLoading(active, label = "Loading…") {
  const ctx = useContext(PageLoadingContext)
  const held = useRef(false)
  const labelRef = useRef(label)
  labelRef.current = label

  useEffect(() => {
    if (!ctx) return undefined

    if (active && !held.current) {
      ctx.begin(labelRef.current)
      held.current = true
    } else if (!active && held.current) {
      ctx.end()
      held.current = false
    }

    return () => {
      if (held.current) {
        ctx.end()
        held.current = false
      }
    }
  }, [active, ctx])
}
